





























import Vue from 'vue'
import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options'
import UiLabel from '@/components/ui/UiLabel.vue'
import { IMaskComponent } from 'vue-imask'

interface Data {
  currentValue: string
  slideLineOffset: number
  slideLineDuration: number
}

interface Methods {
  onEnter: () => void
  onChange: (value: any) => void
  onInput: (value: any) => void
  computeCurrentPosition: (posX: number) => number
  computeCurrentValue: (value: number) => number
  convertPixelsToValue: (value: number) => number
  convertValueToPixels: (value: number) => number
  checkLimits: (value: number) => number
  setValue: (value: number) => void
  setPosition: (value: number) => void
  updateSlideLineMainParams: () => void
  updateSliderParams: () => void
  onLineClick: (e: any) => void
}

interface Computed {
  slideLineValDuration: number
  digitsAfterDecimalPointInStep: number
  maskWithLimits: any
  refTwinPoint: HTMLElement
  refPointBox: HTMLElement
  refProgress: HTMLElement
  refSlideLine: HTMLElement
}

interface Props {
  value: number | string
  mask: any
  start: number
  end: number
  startLabel: string
  endLabel: string
  step: number
  keyValueBlock: string
}

export default Vue.extend({
  name: 'UiSlider',
  components: {
    MaskedInput: IMaskComponent,
    UiLabel
  },
  model: {
    prop: 'value',
    event: 'select'
  },
  props: {
    value: {
      type: [Number, String],
      default: () => ''
    },
    mask: {
      type: Object,
      default: () => {}
    },
    start: {
      type: Number,
      default: 0
    },
    end: {
      type: Number,
      default: 0
    },
    startLabel: {
      type: String,
      default: () => ''
    },
    endLabel: {
      type: String,
      default: () => ''
    },
    step: {
      type: Number,
      default: () => 1
    },
    keyValueBlock: {
      type: String,
      default: () => ''
    }
  },
  data () {
    return {
      currentValue: this.value,
      slideLineOffset: 0,
      slideLineDuration: 0
    }
  },
  computed: {
    slideLineValDuration () { // Длина отрезка значений
      return this.end - this.start
    },
    digitsAfterDecimalPointInStep () {
      return String(this.step).split('.')[1]
        ? String(this.step).split('.')[1].length
        : 0
    },
    maskWithLimits () { // Добавление в маску значений max и min исходя из лимитов в пропсах
      const mask = { ...this.mask }
      if (mask.blocks && mask.blocks[this.keyValueBlock]) {
        mask.blocks[this.keyValueBlock].max = this.end
        mask.blocks[this.keyValueBlock].min = this.start
      }
      mask.max = this.end
      mask.min = this.start
      return mask
    },
    refTwinPoint () {
      return (this.$refs.twinPoint as HTMLElement)
    },
    refPointBox () {
      return (this.$refs.pointBox as HTMLElement)
    },
    refProgress () {
      return (this.$refs.progress as HTMLElement)
    },
    refSlideLine () {
      return (this.$refs.slideLine as HTMLElement)
    }
  },
  watch: {
    currentValue: {
      handler (value: string) {
        if (value === '' || Number(this.currentValue) < this.start) return // Отмена обработки при вводе пустого значения
        // Обработка изменения выбранного значения для изменения отрезка
        this.$nextTick(() => {
          this.setPosition(this.convertValueToPixels(Number(value)) + this.slideLineOffset)
        })
      },
      immediate: true
    },
    value (value: string) {
      this.currentValue = value
    }
  },
  mounted () {
    this.slideLineDuration = this.refSlideLine.offsetWidth // Записываем ширину линии слайдера
    // Подписываемся на событие ресайза и обновляем параметры слайдера
    window.addEventListener('resize', this.updateSliderParams)
    // Если переданное значение меньше минимума - присваиваем значение минимума
    if (Number(this.currentValue) < this.start) this.currentValue = String(this.start)
    // Слушатель события начала перетягивания фейковой точки(twinPoint) на отрезке
    this.refTwinPoint.ondragstart = (event: any) => {
      // Актуализация координатного смещения от левого края экрана
      this.updateSliderParams()
    }
    // Слушатель события перетягивания фейковой точки(twinPoint) на отрезке
    this.refTwinPoint.ondrag = (event: any) => {
      // Обработка вырожденного сигнала события drag при отпускании элемента
      if (event.clientX <= 0) {
        return
      }
      // Изменение значения, позиции точки и ширины линии прогресса
      this.setValue(event.clientX)
    }
    // Слушатель события окончания перетягивания фейковой точки(twinPoint) на отрезке
    this.refTwinPoint.ondragend = (event: any) => {
      this.$emit('change', this.currentValue)
    }
    // Слушатель события начала перетягивания точки(pointBox) на отрезке для touch устройств
    this.refPointBox.ontouchstart = (event: any) => {
      event.preventDefault()
      // Актуализация координатного смещения от левого края экрана
      this.updateSliderParams()
    }
    // Слушатель события перетягивания точки(pointBox) на отрезке для touch устройств
    this.refPointBox.ontouchmove = (event: any) => {
      event.preventDefault()
      // Обработка вырожденного сигнала события drag при отпускании элемента
      if (event.changedTouches[0].clientX <= 0) {
        return
      }
      // Изменение значения, позиции точки и ширины линии прогресса
      this.setValue(event.changedTouches[0].clientX)
    }
    // Слушатель события окончания перетягивания точки(pointBox) на отрезке для touch устройств
    this.refPointBox.ontouchend = (event: any) => {
      this.$emit('change', this.currentValue)
    }
  },
  beforeDestroy () {
    // Отписываемся от события ресайза
    window.removeEventListener('resize', this.updateSliderParams)
  },
  methods: {
    onInput (value): void {
      this.$emit('select', value)
    },
    onEnter (): void {
      this.$emit('onEnter')
    },
    onChange (e: any): void {
      // Обработка ситуации, когда пользователь ввёл пустое значение и покинул поле ввода
      if (this.currentValue === '' || Number(this.currentValue) < this.start) {
        this.currentValue = String(this.start)
      }
      this.$emit('change', this.currentValue)
    },
    computeCurrentPosition (posX: number) { // Расчёт расстояния переданной координаты относитетельно отрезка
      return posX - this.slideLineOffset
    },
    computeCurrentValue (value: number) { // Расчёт выбранного значения
      return Number(this.convertPixelsToValue(value).toFixed(this.digitsAfterDecimalPointInStep))
    },
    convertPixelsToValue (value: number) { // Перевод пиксельного значения в значения отрезка
      return this.start + this.slideLineValDuration * value / this.slideLineDuration
    },
    convertValueToPixels (value: number) { // Перевод значения отрезка в пиксельное значение
      return (value - this.start) * this.slideLineDuration / this.slideLineValDuration
    },
    checkLimits (value: number): number { // Метод контроля выхода точки за пределы
      // Выход точки за левый предел отрезка
      if (this.computeCurrentPosition(value) <= 0) {
        return this.slideLineOffset
      }
      // Выход точки за правый предел отрезка
      if (this.computeCurrentPosition(value) >= this.slideLineDuration) {
        return this.slideLineOffset + this.slideLineDuration
      }
      // Точка движется внутри отрезка
      return value
    },
    setValue (value: number) { // Изменение значения соответствующего положению точки
      // Расчитываем новое значение, соответствующее позиции точке на отрезке
      const newValue = this.computeCurrentValue(this.computeCurrentPosition(this.checkLimits(value)))
      // Присваивание значений текствого поля с учётом шага и направленности изменения
      if (newValue >= Number(this.currentValue)) {
        this.currentValue = String((Math.floor(newValue / this.step) * this.step).toFixed(this.digitsAfterDecimalPointInStep))
      }
      if (newValue < Number(this.currentValue)) {
        this.currentValue = String((Math.ceil(newValue / this.step) * this.step).toFixed(this.digitsAfterDecimalPointInStep))
      }
    },
    setPosition (value: number) { // Изменение положения точки и ширины прогресс бара на отрезке
      const validValue = this.checkLimits(value)
      // Изменяем позицию точки и ширину линии прогресса исходя из позиции фейковой точки на отрезке
      this.refProgress.style.width = `${Math.round(this.computeCurrentPosition(validValue))}px`
      this.refPointBox.style.transform = `translateX(${Math.round(this.computeCurrentPosition(validValue))}px)`
    },
    updateSlideLineMainParams () { // Метод актуализации основных параметров слайдера
      // Актуализация записанной ширины линии слайдера - ширине текущей
      if (this.slideLineOffset !== this.refSlideLine.getBoundingClientRect().left) {
        this.slideLineDuration = this.refSlideLine.offsetWidth
      }
      // Актуализация записанной координаты - координате текущей
      if (this.slideLineOffset !== this.refSlideLine.getBoundingClientRect().left) {
        this.slideLineOffset = this.refSlideLine.getBoundingClientRect().left
      }
    },
    updateSliderParams () {
      // Актуализация основных параметров слайдера
      this.updateSlideLineMainParams()
      // Перемещение точки исходя из новых параметров слайдера
      this.setPosition(this.convertValueToPixels(Number(this.currentValue)) + this.slideLineOffset)
    },
    onLineClick (e: any) { // Обработка клика на отрезке
      // Актуализация координатного смещения от левого края экрана
      this.updateSlideLineMainParams()
      // Изменение значения, позиции точки и ширины линии прогресса
      this.setValue(e.clientX)
      this.$nextTick(() => {
        this.$emit('change', this.currentValue)
      })
    }
  }
} as ThisTypedComponentOptionsWithRecordProps<Vue, Data, Methods, Computed, Props>)
